import {
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Copy,
  Lock,
  Plus,
  Send,
  Users,
  Wand2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { messageForCode } from '@obraplan/core/application/errors/error-codes';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { IsoWeek } from '@obraplan/core/domain/date/iso-week';
import type { CapacityConflict } from '@obraplan/core/domain/planning/allocation-conflict';
import { PLANNING_ERROR } from '@obraplan/core/domain/planning/errors';
import { sortRows, type BoardSort } from '@obraplan/core/domain/planning/weekly-board';
import { WEEKLY_PLAN_STATUS_LABEL } from '@obraplan/core/domain/planning/weekly-plan';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { formatPercent } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { AllocationPanel, type AllocationSubmit } from '@/features/planning/AllocationPanel';
import { AutoAllocationBanner, AutoAllocationPanel } from '@/features/planning/AutoAllocationPanel';
import { CopyWeekDialog } from '@/features/planning/CopyWeekDialog';
import { AvailabilityPanel } from '@/features/planning/AvailabilityPanel';
import { CapacityOverrideDialog } from '@/features/planning/CapacityOverrideDialog';
import { TeamAllocationDialog } from '@/features/planning/TeamAllocationDialog';
import {
  teamAllocationTeams,
  type TeamAllocationRefusal,
  type TeamAllocationSubmit,
} from '@/features/planning/team-allocation';
import { WeeklyPlannerCards } from '@/features/planning/WeeklyPlannerCards';
import { WeeklyPlannerGrid, type AllocationEdit } from '@/features/planning/WeeklyPlannerGrid';
import { ProjectVersionNotice } from '@/features/projects/ProjectVersionNotice';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/**
 * Planejamento semanal (Q01 a Q05): o que fazer, quem faz, quantas horas, quem tem saldo e
 * quem está sobrecarregado. A tela avisa sobre o excesso; quem recusa é o caso de uso
 * (RN-134), e o diálogo de exceção abre com os números que vieram do domínio.
 */
export function WeeklyPlanPage() {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const canPlan = usePermission(PERMISSION.LABOR_PLAN);
  const canOverride = usePermission(PERMISSION.LABOR_ALLOCATE_OVERCAPACITY);

  const [week, setWeek] = useState(() => IsoWeek.of(container.clock.today('America/Sao_Paulo')));
  const [sort, setSort] = useState<BoardSort>('BALANCE');
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelResourceId, setPanelResourceId] = useState<string | undefined>(undefined);
  const [copyOpen, setCopyOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [teamPreset, setTeamPreset] = useState<string | undefined>(undefined);
  const [pendingOverride, setPendingOverride] = useState<
    | {
        conflict: CapacityConflict | undefined;
        resourceName: string;
        retry: (justification: string) => void;
      }
    | undefined
  >(undefined);

  const view = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getWeeklyBoard.execute(
            { projectId: activeProjectId, isoYear: week.isoYear, isoWeek: week.isoWeek },
            session,
          ),
    [session?.userId, activeProjectId, week.key()],
  );

  // A proposta do cronograma, carregada junto com o quadro — a pergunta "o que o cronograma
  // manda esta semana?" é a primeira da tela. Ler não grava nada (RN-089).
  const suggestion = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.suggestScheduleAllocation.execute(
            { projectId: activeProjectId, isoYear: week.isoYear, isoWeek: week.isoWeek },
            session,
          ),
    [session?.userId, activeProjectId, week.key()],
  );

  const proposal =
    suggestion !== undefined && suggestion.ok ? suggestion.value.proposal : undefined;

  // O modo de medição da obra (Fase 30): na obra por equipe, a equipe também se planeja.
  const project = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getProject.execute(activeProjectId, session),
    [session?.userId, activeProjectId],
  );
  const porEquipe =
    project !== undefined && project.ok && project.value.measurementMode === 'POR_EQUIPE';

  // Quem está em cada equipe em cada dia (RN-053) — o diálogo mostra antes quem vai receber.
  // Só na obra por equipe e para quem planeja: é a leitura inteira da mão de obra.
  const workforce = useLive(
    () =>
      session === undefined || activeProjectId === undefined || !porEquipe || !canPlan
        ? Promise.resolve(undefined)
        : container.getWorkforce.execute(activeProjectId, session),
    [session?.userId, activeProjectId, porEquipe, canPlan],
  );

  // O que a cópia faria, sem gravar. Carregada só com o diálogo aberto: é a leitura completa
  // da semana anterior, e a maioria não usa a ação.
  const copyPreview = useLive(
    () =>
      session === undefined || activeProjectId === undefined || !copyOpen
        ? Promise.resolve(undefined)
        : container.copyPreviousWeek.execute(
            {
              projectId: activeProjectId,
              isoYear: week.isoYear,
              isoWeek: week.isoWeek,
              preview: true,
            },
            session,
          ),
    [session?.userId, activeProjectId, week.key(), copyOpen],
  );

  const copyPlan = copyPreview !== undefined && copyPreview.ok ? copyPreview.value : undefined;

  const data = view !== undefined && view.ok ? view.value : undefined;

  const rows = useMemo(() => {
    if (data === undefined) return [];
    // A ordenação é do domínio; a tela só escolhe o critério.
    return data.board.rows;
  }, [data]);

  // A única recusa que esta tela responde: estourar a capacidade (RN-045). Remanejar antes
  // de o dia acontecer não pede motivo; quem presta contas do que mudou é a Conferência.
  const handleCapacityRefusal = (
    code: string,
    details: Readonly<Record<string, unknown>> | undefined,
    resourceName: string,
    retry: (justification: string) => void,
  ): boolean => {
    const isCapacity =
      code === PLANNING_ERROR.CAPACITY_OVERRIDE_NEEDS_REASON ||
      code === PLANNING_ERROR.CAPACITY_OVERRIDE_FORBIDDEN;
    if (!isCapacity) return false;

    setPendingOverride({
      conflict: details?.conflict as CapacityConflict | undefined,
      resourceName,
      retry,
    });
    return true;
  };

  const [allocate, allocateState] = useCommand(
    (input: AllocationSubmit & { justification?: string }) =>
      container.createWeeklyAllocation.execute(
        {
          projectId: activeProjectId,
          isoYear: week.isoYear,
          isoWeek: week.isoWeek,
          ...input,
        },
        session!,
      ),
    {
      successMessage: 'Alocação registrada.',
      silent: true,
      onSuccess: () => {
        setPanelOpen(false);
        setPendingOverride(undefined);
      },
    },
  );

  const [updateAllocation, updateState] = useCommand(
    (input: {
      allocationId: string;
      quarterHours: number;
      version: number;
      justification?: string;
      plannedQuantity?: number | null;
    }) =>
      container.updateWeeklyAllocation.execute(
        {
          allocationId: input.allocationId,
          quarterHours: input.quarterHours,
          expectedVersion: input.version,
          ...(input.justification === undefined ? {} : { justification: input.justification }),
          // `undefined` deixa a meta como está; `null` limpa. Espalhar o campo sempre
          // apagaria a medição a cada edição de horas.
          ...(input.plannedQuantity === undefined
            ? {}
            : { plannedQuantity: input.plannedQuantity }),
        },
        session!,
      ),
    {
      // A mesma operação grava horas e meta: dizer 'horas atualizadas' depois de mexer
      // só na medição seria avisar sobre o que não mudou.
      successMessage: 'Alocação atualizada.',
      silent: true,
      onSuccess: () => setPendingOverride(undefined),
    },
  );

  const [removeAllocation, removeState] = useCommand(
    (input: AllocationEdit & { justification?: string }) =>
      container.deleteWeeklyAllocation.execute(
        {
          allocationId: input.allocationId,
          expectedVersion: input.version,
          ...(input.justification === undefined ? {} : { justification: input.justification }),
        },
        session!,
      ),
    {
      successMessage: 'Alocação removida.',
      silent: true,
      onSuccess: () => setPendingOverride(undefined),
    },
  );

  // Alocar a equipe (Fase 30, RN-423). A recusa aparece dentro do diálogo, com o nome de quem
  // a causou; a da hora extra pede o motivo lá mesmo e reenvia.
  const [allocateTeam] = useCommand(
    (input: TeamAllocationSubmit) =>
      container.createTeamAllocation.execute(
        { projectId: activeProjectId, isoYear: week.isoYear, isoWeek: week.isoWeek, ...input },
        session!,
      ),
    {
      // A equipe já costuma se chamar "Equipe Alvenaria": o prefixo só entra quando falta.
      successMessage: (result) =>
        `${/^equipe\b/i.test(result.teamName) ? '' : 'Equipe '}${result.teamName} alocada · ${String(result.summary.people)} ${
          result.summary.people === 1 ? 'pessoa' : 'pessoas'
        } · ${Hours.ofQuarters(result.summary.quarterHours).format()}`,
      silent: true,
      onSuccess: () => setTeamOpen(false),
    },
  );

  const submitTeam = async (
    input: TeamAllocationSubmit,
  ): Promise<TeamAllocationRefusal | undefined> => {
    const result = await allocateTeam(input);
    if (result === undefined) {
      return { code: 'SYSTEM.UNEXPECTED', message: messageForCode('SYSTEM.UNEXPECTED') };
    }
    return result.ok ? undefined : { code: result.error.code, message: result.error.message };
  };

  const [applyAuto, applyAutoState] = useCommand(
    (resourceIds: readonly string[] | undefined) =>
      container.applyScheduleAllocation.execute(
        {
          projectId: activeProjectId,
          isoYear: week.isoYear,
          isoWeek: week.isoWeek,
          ...(resourceIds === undefined ? {} : { resourceIds: [...resourceIds] }),
        },
        session!,
      ),
    {
      successMessage: (result) =>
        result.createdCount === 0
          ? 'Nada foi alocado — a proposta já estava aplicada ou não coube na jornada.'
          : `${String(result.createdCount)} alocações criadas para ${String(result.resourceCount)} funcionários${
              // Na obra por equipe, a marca da equipe foi junto (RN-432): a grade já as agrupa.
              result.teamCount > 0
                ? `, em ${String(result.teamCount)} ${result.teamCount === 1 ? 'equipe' : 'equipes'}`
                : ''
            }.`,
      onSuccess: () => setAutoOpen(false),
    },
  );

  const [changeStatus] = useCommand(
    (status: 'DRAFT' | 'PUBLISHED' | 'CLOSED') =>
      container.changeWeeklyPlanStatus.execute(
        { projectId: activeProjectId, isoYear: week.isoYear, isoWeek: week.isoWeek, status },
        session!,
      ),
    { successMessage: (plan) => `Semana ${WEEKLY_PLAN_STATUS_LABEL[plan.status].toLowerCase()}.` },
  );

  const [copyWeek, copyState] = useCommand(
    () =>
      container.copyPreviousWeek.execute(
        { projectId: activeProjectId, isoYear: week.isoYear, isoWeek: week.isoWeek },
        session!,
      ),
    {
      // Três desfechos, três frases: "não havia o que copiar", "não coube nada" e "copiei
      // tanto" — a mensagem única de antes fazia a recusa do excesso parecer bug.
      successMessage: (result) => {
        if (!result.sourceHasAllocations) {
          return `A semana ${String(result.sourceIsoWeek)} não tem alocações para copiar.`;
        }
        if (result.copiedCount === 0) {
          return `Nada foi copiado: as ${String(result.skippedCount)} alocações não cabem na jornada desta semana.`;
        }
        return `${String(result.copiedCount)} alocações copiadas${result.skippedCount > 0 ? `, ${String(result.skippedCount)} puladas por não caberem` : ''}.`;
      },
      onSuccess: (result) => {
        if (result.copiedCount > 0) setCopyOpen(false);
      },
    },
  );

  if (activeProjectId === undefined) {
    return (
      <EmptyState
        title="Nenhuma obra selecionada"
        description="Escolha uma obra no seletor do topo para planejar a semana."
        icon={<CalendarRange className="size-8" />}
      />
    );
  }

  if (view === undefined) return <LoadingState label="Carregando a semana…" />;
  if (!view.ok) return <ErrorState message={view.error.message} code={view.error.code} />;
  if (data === undefined) return <LoadingState />;

  const status = data.plan?.status ?? 'DRAFT';
  const readOnly = status === 'CLOSED';
  const summary = data.board.summary;

  const submitAllocation = async (input: AllocationSubmit, justification?: string) => {
    const result = await allocate({
      ...input,
      ...(justification === undefined ? {} : { justification }),
    });
    if (result !== undefined && !result.ok) {
      const resourceName =
        rows.find((row) => row.resource.resourceId === input.resourceId)?.resource.name ??
        'O recurso';
      const handled = handleCapacityRefusal(
        result.error.code,
        result.error.details,
        resourceName,
        (reason) => {
          void submitAllocation(input, reason);
        },
      );
      // Com o diálogo aberto, a recusa aparece dentro dele. Pela célula da grade não há
      // diálogo: sem o aviso, a hora digitada ficava no campo com cara de gravada.
      if (!handled && !panelOpen) toast.error(result.error.message);
    }
  };

  const submitEdit = async (edit: AllocationEdit, justification?: string) => {
    const result = await updateAllocation({
      allocationId: edit.allocationId,
      quarterHours: edit.quarterHours,
      version: edit.version,
      ...(justification === undefined ? {} : { justification }),
      ...(edit.plannedQuantity === undefined ? {} : { plannedQuantity: edit.plannedQuantity }),
    });
    if (result !== undefined && !result.ok) {
      const owner = rows.find((row) =>
        row.activities.some((activityRow) =>
          activityRow.cells.some((cell) => cell.allocation?.id === edit.allocationId),
        ),
      );
      const handled = handleCapacityRefusal(
        result.error.code,
        result.error.details,
        owner?.resource.name ?? 'O recurso',
        (reason) => {
          void submitEdit(edit, reason);
        },
      );
      // Conflito de versão e semana fechada não abrem diálogo: são avisos diretos, e o
      // quadro reverte sozinho — o `useLive` traz de volta o valor que está gravado.
      if (!handled) toast.error(result.error.message);
    }
  };

  /** RN-073 — excluir é alterar, e a partir da segunda do dia pede o motivo como as demais. */
  const submitDelete = async (edit: AllocationEdit, justification?: string) => {
    const result = await removeAllocation({
      ...edit,
      ...(justification === undefined ? {} : { justification }),
    });
    if (result !== undefined && !result.ok) {
      const owner = rows.find((row) =>
        row.activities.some((activityRow) =>
          activityRow.cells.some((cell) => cell.allocation?.id === edit.allocationId),
        ),
      );
      const handled = handleCapacityRefusal(
        result.error.code,
        result.error.details,
        owner?.resource.name ?? 'O recurso',
        (reason) => {
          void submitDelete(edit, reason);
        },
      );
      if (!handled) toast.error(result.error.message);
    }
  };

  /** Montado uma vez: a grade e os cartões recebem exatamente os mesmos dados e gestos. */
  const plannerProps = {
    board: { ...data.board, rows: sortRows(data.board.rows, sort) },
    sort,
    onSortChange: setSort,
    readOnly,
    canPlan,
    onCreate: (input: {
      resourceId: string;
      activityId: string;
      serviceId?: string;
      date: string;
      quarterHours: number;
    }) =>
      void submitAllocation({
        resourceId: input.resourceId,
        activityId: input.activityId,
        ...(input.serviceId === undefined ? {} : { serviceId: input.serviceId }),
        days: [{ date: input.date, quarterHours: input.quarterHours }],
      }),
    onEdit: (edit: AllocationEdit) => void submitEdit(edit),
    onDelete: (edit: AllocationEdit) => void submitDelete(edit),
    onAdd: (resourceId: string) => {
      setPanelResourceId(resourceId);
      setPanelOpen(true);
    },
    ...(porEquipe
      ? {
          onAddTeam: (teamId: string) => {
            setTeamPreset(teamId);
            setTeamOpen(true);
          },
        }
      : {}),
  };

  return (
    <div className="flex flex-col gap-6">
      <ProjectVersionNotice />
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Planejamento semanal</h1>
          <p className="text-(--color-content-muted)">
            O que precisa ser feito, quem faz e por quantas horas — com controle real de capacidade.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canPlan && !readOnly && (
            <>
              <Button variant="secondary" onClick={() => setAutoOpen(true)}>
                <Wand2 aria-hidden="true" />
                Alocar pelo cronograma
              </Button>
              <Button variant="secondary" onClick={() => setCopyOpen(true)}>
                <Copy aria-hidden="true" />
                Copiar semana anterior
              </Button>
              <Button
                variant="secondary"
                onClick={() => void changeStatus(status === 'DRAFT' ? 'PUBLISHED' : 'CLOSED')}
              >
                {status === 'DRAFT' ? <Send aria-hidden="true" /> : <Lock aria-hidden="true" />}
                {status === 'DRAFT' ? 'Publicar semana' : 'Fechar semana'}
              </Button>
              {porEquipe && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setTeamPreset(undefined);
                    setTeamOpen(true);
                  }}
                >
                  <Users aria-hidden="true" />
                  Alocar equipe
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => {
                  setPanelResourceId(undefined);
                  setPanelOpen(true);
                }}
              >
                <Plus aria-hidden="true" />
                Nova alocação
              </Button>
            </>
          )}
          {canPlan && readOnly && (
            <Button variant="secondary" onClick={() => void changeStatus('PUBLISHED')}>
              Reabrir semana
            </Button>
          )}
        </div>
      </header>

      {/* ── Navegação por semana ────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Semana anterior"
            onClick={() => setWeek(week.previous())}
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          <span className="min-w-64 text-center font-medium">{data.weekLabel}</span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Próxima semana"
            onClick={() => setWeek(week.next())}
          >
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setWeek(IsoWeek.of(container.clock.today('America/Sao_Paulo')))}
        >
          Semana atual
        </Button>

        <Badge tone={status === 'CLOSED' ? 'neutral' : status === 'PUBLISHED' ? 'info' : 'warn'}>
          {WEEKLY_PLAN_STATUS_LABEL[status]}
        </Badge>
      </div>

      {/* ── Cartões de resumo ───────────────────────────────────────────────── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {/* Os quatro primeiros cartões são da mão de obra própria: a capacidade de uma
            empreiteira é entrega contratada, não hora a ocupar, e não entra no saldo. */}
        <SummaryCard
          label="Capacidade"
          value={Hours.ofQuarters(summary.availableQuarterHours).format()}
          hint="Jornada dos funcionários aptos"
        />
        <SummaryCard
          label="Planejado"
          value={Hours.ofQuarters(summary.plannedQuarterHours).format()}
          hint={
            summary.servicePlannedQuarterHours > 0
              ? `${String(summary.allocatedResourceCount)} funcionários · + ${Hours.ofQuarters(summary.servicePlannedQuarterHours).format()} em ${String(summary.serviceCount)} ${summary.serviceCount === 1 ? 'serviço' : 'serviços'}`
              : `${String(summary.allocatedResourceCount)} funcionários com alocação`
          }
        />
        <SummaryCard
          label="Saldo"
          value={Hours.ofQuarters(summary.balanceQuarterHours).format()}
          hint="Hora de funcionário ainda livre"
          tone={summary.balanceQuarterHours < 0 ? 'danger' : 'ok'}
        />
        <SummaryCard
          label="Ociosidade"
          value={formatPercent(summary.idleRatio)}
          hint="Da jornada dos funcionários"
        />
        <SummaryCard
          label="Sobrecarregados"
          value={String(summary.overloadedCount)}
          hint={`${String(summary.atLimitCount)} no limite`}
          tone={summary.overloadedCount > 0 ? 'danger' : 'ok'}
        />
      </div>

      {canPlan && !readOnly && (
        <AutoAllocationBanner
          proposal={proposal}
          byTeam={porEquipe}
          onOpen={() => setAutoOpen(true)}
        />
      )}

      {/* A mesma semana em duas apresentações — grade no monitor, cartões no celular: sete
          colunas obrigariam a arrastar até perder de vista quem se está planejando. */}
      <div className="hidden md:block">
        <WeeklyPlannerGrid {...plannerProps} />
      </div>
      <div className="md:hidden">
        <WeeklyPlannerCards {...plannerProps} />
      </div>

      <AvailabilityPanel availability={data.availability} />

      <AllocationPanel
        open={panelOpen}
        onOpenChange={setPanelOpen}
        rows={rows}
        days={data.board.days}
        activities={data.allocatableActivities}
        services={data.board.services}
        {...(panelResourceId === undefined ? {} : { initialResourceId: panelResourceId })}
        isPending={allocateState.isPending}
        {...(allocateState.error === undefined
          ? {}
          : { errorMessage: allocateState.error.message })}
        onSubmit={(input) => void submitAllocation(input)}
      />

      {porEquipe && (
        <TeamAllocationDialog
          open={teamOpen}
          onOpenChange={setTeamOpen}
          teams={teamAllocationTeams(
            data.board.teams,
            data.board.days.map((day) => day.date),
            workforce !== undefined && workforce.ok ? workforce.value : undefined,
          )}
          days={data.board.days}
          activities={data.allocatableActivities}
          services={data.board.services}
          {...(teamPreset === undefined ? {} : { initialTeamId: teamPreset })}
          onSubmit={submitTeam}
        />
      )}

      <AutoAllocationPanel
        open={autoOpen}
        onOpenChange={setAutoOpen}
        proposal={proposal}
        weekLabel={data.weekLabel}
        days={data.board.days}
        readOnly={readOnly || !canPlan}
        isPending={applyAutoState.isPending}
        byTeam={porEquipe}
        onApply={(resourceIds) => void applyAuto(resourceIds)}
      />

      <CapacityOverrideDialog
        open={pendingOverride !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingOverride(undefined);
        }}
        conflict={pendingOverride?.conflict}
        resourceName={pendingOverride?.resourceName ?? 'O recurso'}
        canOverride={canOverride}
        isPending={allocateState.isPending || updateState.isPending || removeState.isPending}
        onConfirm={(justification) => pendingOverride?.retry(justification)}
      />

      <CopyWeekDialog
        open={copyOpen}
        onOpenChange={setCopyOpen}
        preview={copyPlan}
        resources={data.resources}
        activities={data.allocatableActivities}
        isPending={copyState.isPending}
        onConfirm={() => void copyWeek()}
      />
    </div>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
  hint: string;
  tone?: 'ok' | 'danger';
}

function SummaryCard({ label, value, hint, tone }: SummaryCardProps) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-0.5 p-4">
        <p className="text-xs uppercase tracking-wide text-(--color-content-muted)">{label}</p>
        <p
          className={
            tone === 'danger'
              ? 'tabular text-xl font-semibold text-(--color-state-danger)'
              : 'tabular text-xl font-semibold'
          }
        >
          {value}
        </p>
        <p className="text-xs text-(--color-content-muted)">{hint}</p>
      </CardContent>
    </Card>
  );
}
